/**
 * Public Profile Settings Page
 *
 * Allows users to manage their public profile visibility settings,
 * including slug, display name, visibility toggles for certifications and NPC progress.
 */

import { useState, useEffect } from 'react';
import { serverMessageOf } from '../utils/billingStatusView';
import { Link } from 'react-router-dom';
import {
  useGetMyPublicProfileSettingsQuery,
  useUpdateMyPublicProfileSettingsMutation,
  usePublishMyProfileMutation,
  useUnpublishMyProfileMutation,
} from '../store/api/portfolioApi';
import { MyEndorsementsSection } from '../components/endorsements/MyEndorsementsSection';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';
import { DiscoveryOptInCard } from '../components/profile/DiscoveryOptInToggle';
import { ShareLinksManager } from '../components/profile/ShareLinksManager';

export function PublicProfileSettingsPage() {
  const { data: settings, isLoading, refetch } = useGetMyPublicProfileSettingsQuery();
  // Manager endorsement is dark until the server turns it on (FEATURE_ENDORSEMENTS).
  const { data: serverCaps } = useGetCapabilitiesQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateMyPublicProfileSettingsMutation();
  const [publishProfile, { isLoading: isPublishing }] = usePublishMyProfileMutation();
  const [unpublishProfile, { isLoading: isUnpublishing }] = useUnpublishMyProfileMutation();

  // Form state
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [headline, setHeadline] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [showAuthorityScores, setShowAuthorityScores] = useState(true);
  const [showBadges, setShowBadges] = useState(true);
  const [showFeaturedClips, setShowFeaturedClips] = useState(true);
  const [showCertificationsPublicly, setShowCertificationsPublicly] = useState(true);
  const [showNpcProgressPublicly, setShowNpcProgressPublicly] = useState(true);

  const [slugError, setSlugError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  /** A refused publish, in the server's words (e.g. the eligibility rule) — never a bare "Failed". */
  const [publishError, setPublishError] = useState<string | null>(null);

  // Initialize form state from settings
  useEffect(() => {
    if (settings) {
      setSlug(settings.slug || '');
      setDisplayName(settings.displayName || '');
      setHeadline(settings.headline || '');
      setBio(settings.bio || '');
      setLocation(settings.location || '');
      setShowAuthorityScores(settings.visibility.showAuthorityScores);
      setShowBadges(settings.visibility.showBadges);
      setShowFeaturedClips(settings.visibility.showFeaturedClips);
      setShowCertificationsPublicly(settings.visibility.showCertificationsPublicly);
      setShowNpcProgressPublicly(settings.visibility.showNpcProgressPublicly);
    }
  }, [settings]);

  const validateSlug = (value: string): boolean => {
    if (!value) {
      setSlugError(null);
      return true;
    }
    const slugRegex = /^[a-z0-9][a-z0-9_-]{1,48}[a-z0-9]$/;
    if (!slugRegex.test(value.toLowerCase())) {
      setSlugError('Must be 3-50 characters: lowercase letters, numbers, hyphens, or underscores');
      return false;
    }
    setSlugError(null);
    return true;
  };

  const handleSlugChange = (value: string) => {
    const normalized = value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
    setSlug(normalized);
    validateSlug(normalized);
  };

  const handleSaveSettings = async () => {
    if (!validateSlug(slug)) return;

    try {
      await updateSettings({
        slug: slug || null,
        displayName: displayName || null,
        headline: headline || null,
        bio: bio || null,
        location: location || null,
        showAuthorityScores,
        showBadges,
        showFeaturedClips,
        showCertificationsPublicly,
        showNpcProgressPublicly,
      }).unwrap();
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      refetch();
    } catch (err: any) {
      if (err?.data?.message?.includes('slug')) {
        setSlugError(err.data.message);
      } else {
        alert('Failed to save settings');
      }
    }
  };

  const handlePublish = async () => {
    if (!slug) {
      setSlugError('A profile URL is required to publish');
      return;
    }
    setPublishError(null);
    try {
      await publishProfile().unwrap();
      setSuccessMessage('Profile published!');
      setTimeout(() => setSuccessMessage(null), 3000);
      refetch();
    } catch (err) {
      // A 403 is the eligibility rule (canonical performance score): say it as
      // the server words it. Anything else is a failure to publish, said so.
      setPublishError(serverMessageOf(err) ?? 'Your profile could not be published right now. Try again.');
    }
  };

  const handleUnpublish = async () => {
    try {
      await unpublishProfile().unwrap();
      setSuccessMessage('Profile unpublished');
      setTimeout(() => setSuccessMessage(null), 3000);
      refetch();
    } catch (err) {
      alert('Failed to unpublish profile');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  const isPublished = settings?.isPublic;
  const publicUrl = settings?.publicUrl;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Public Profile Settings</h1>
          <p className="mt-1 text-gray-500">
            Control how your profile appears to others
          </p>
        </div>
        {publicUrl && (
          <Link
            to={publicUrl}
            target="_blank"
            className="btn btn-outline flex items-center gap-2"
          >
            <ExternalLinkIcon className="w-4 h-4" />
            View Profile
          </Link>
        )}
      </div>

      {publishError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg" role="alert" data-testid="publish-error">
          {publishError}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Publish Status */}
      <div className={`card ${isPublished ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
        <div className="card-body flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPublished ? 'bg-green-100' : 'bg-gray-100'}`}>
              {isPublished ? (
                <GlobeIcon className="w-5 h-5 text-green-600" />
              ) : (
                <LockIcon className="w-5 h-5 text-gray-500" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {isPublished ? 'Your profile is public' : 'Your profile is private'}
              </h3>
              <p className="text-sm text-gray-500">
                {isPublished
                  ? `Visible at ${window.location.origin}${publicUrl}`
                  : 'Only you can see your profile'}
              </p>
            </div>
          </div>
          {isPublished ? (
            <button
              onClick={handleUnpublish}
              disabled={isUnpublishing}
              className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50"
            >
              {isUnpublishing ? 'Unpublishing...' : 'Unpublish'}
            </button>
          ) : (
            <button
              onClick={handlePublish}
              disabled={isPublishing || !slug}
              className="btn btn-primary"
            >
              {isPublishing ? 'Publishing...' : 'Publish Profile'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile URL */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900">Profile URL</h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username / Slug
              </label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">{window.location.origin}/u/</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="your-username"
                  className={`input flex-1 ${slugError ? 'border-red-500' : ''}`}
                  maxLength={50}
                />
              </div>
              {slugError && (
                <p className="text-red-500 text-sm mt-1">{slugError}</p>
              )}
              <p className="text-gray-400 text-xs mt-1">
                3-50 characters: lowercase letters, numbers, hyphens, or underscores
              </p>
            </div>
          </div>
        </div>

        {/* Display Info */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900">Display Information</h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                className="input"
                maxLength={255}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Headline
              </label>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="e.g., Senior Product Manager | Pitch Coach"
                className="input"
                maxLength={500}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., San Francisco, CA"
                className="input"
                maxLength={255}
              />
            </div>
          </div>
        </div>

        {/* Bio */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900">Bio</h3>
          </div>
          <div className="card-body">
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell visitors about yourself..."
              className="input min-h-[120px]"
              maxLength={5000}
            />
            <p className="text-gray-400 text-xs mt-1">{bio.length}/5000 characters</p>
          </div>
        </div>

        {/* Visibility Settings */}
        <div className="card lg:col-span-2">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900">Visibility Settings</h3>
            <p className="text-sm text-gray-500 mt-1">
              Control what sections are visible on your public profile
            </p>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <ChartIcon className="w-5 h-5 text-blue-600" />
                  <div>
                    <span className="font-medium text-gray-900">Authority Scores</span>
                    <p className="text-xs text-gray-500">Show your tier and authority score</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={showAuthorityScores}
                  onChange={(e) => setShowAuthorityScores(e.target.checked)}
                  className="toggle"
                />
              </label>

              <label className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <BadgeIcon className="w-5 h-5 text-yellow-600" />
                  <div>
                    <span className="font-medium text-gray-900">Badges</span>
                    <p className="text-xs text-gray-500">Display earned badges</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={showBadges}
                  onChange={(e) => setShowBadges(e.target.checked)}
                  className="toggle"
                />
              </label>

              <label className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <ClipIcon className="w-5 h-5 text-purple-600" />
                  <div>
                    <span className="font-medium text-gray-900">Featured Clips</span>
                    <p className="text-xs text-gray-500">Show your selected pitch samples</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={showFeaturedClips}
                  onChange={(e) => setShowFeaturedClips(e.target.checked)}
                  className="toggle"
                />
              </label>

              <label className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <CertificateIcon className="w-5 h-5 text-green-600" />
                  <div>
                    <span className="font-medium text-gray-900">Certifications</span>
                    <p className="text-xs text-gray-500">Show domain exam certifications</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={showCertificationsPublicly}
                  onChange={(e) => setShowCertificationsPublicly(e.target.checked)}
                  className="toggle"
                />
              </label>

              <label className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <TrophyIcon className="w-5 h-5 text-indigo-600" />
                  <div>
                    <span className="font-medium text-gray-900">NPC Progress</span>
                    <p className="text-xs text-gray-500">Show persona challenge achievements</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={showNpcProgressPublicly}
                  onChange={(e) => setShowNpcProgressPublicly(e.target.checked)}
                  className="toggle"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Endorsements (P4.1): each saved immediately, private unless published */}
        <MyEndorsementsSection
          portfolioIsPublic={settings?.isPublic ?? false}
          featureOn={serverCaps?.endorsements === true}
        />
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveSettings}
          disabled={isUpdating || !!slugError}
          className="btn btn-primary"
        >
          {isUpdating ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Internal talent discovery: an explicit opt-in, separate from publishing.
          Saves immediately (not with "Save Settings"). */}
      <DiscoveryOptInCard isPublic={isPublished === true} />

      {/* Scoped share links */}
      <div className="card">
        <div className="card-body">
          <ShareLinksManager />
        </div>
      </div>
    </div>
  );
}

// Icon components
function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function GlobeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function ClipIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function CertificateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}
